import { Head, Link, router } from '@inertiajs/react'
import { Pencil, Plus, Trash2, Users as UsersIcon } from 'lucide-react'

import Heading from '@/components/heading'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import AppLayout from '@/layouts/app-layout'
import { adminUserPath, adminUsersPath, editAdminUserPath, newAdminUserPath } from '@/routes'
import type { BreadcrumbItem, User } from '@/types'

interface AdminUsersIndexProps {
  users: Pick<User, 'id' | 'name' | 'email' | 'role' | 'created_at'>[]
}

const breadcrumbs: BreadcrumbItem[] = [{ title: 'Users', href: adminUsersPath() }]

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function getRoleBadgeVariant(role?: string): 'default' | 'secondary' | 'destructive' {
  switch (role) {
    case 'superadmin':
      return 'destructive'
    case 'admin':
      return 'default'
    default:
      return 'secondary'
  }
}

function handleDelete(userId: number, userName: string) {
  if (confirm(`Are you sure you want to delete ${userName}? This action cannot be undone.`)) {
    router.delete(adminUserPath(userId))
  }
}

export default function AdminUsersIndex({ users }: AdminUsersIndexProps) {
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Users" />

      <div className="p-6">
        <div className="flex items-center justify-between">
          <Heading title="Users" description="Manage system users and their roles" />
          <Button asChild>
            <Link href={newAdminUserPath()}>
              <Plus className="mr-2 h-4 w-4" />
              Add User
            </Link>
          </Button>
        </div>

        <Card className="mt-8">
          <CardContent className="p-0">
            {users.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <UsersIcon className="text-muted-foreground h-12 w-12" />
                <h3 className="mt-4 text-lg font-semibold">No users yet</h3>
                <p className="text-muted-foreground mt-2 text-sm">
                  Get started by adding your first user
                </p>
                <Button asChild className="mt-4">
                  <Link href={newAdminUserPath()}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add User
                  </Link>
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b">
                    <tr className="bg-muted/50">
                      <th className="px-6 py-3 text-left text-sm font-semibold">Name</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold">Email</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold">Role</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold">Created</th>
                      <th className="px-6 py-3 text-right text-sm font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-muted/50">
                        <td className="px-6 py-4 text-sm font-medium">{user.name}</td>
                        <td className="px-6 py-4 text-sm">{user.email}</td>
                        <td className="px-6 py-4 text-sm">
                          <Badge variant={getRoleBadgeVariant(user.role)}>
                            {user.role ?? 'admin'}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-sm text-muted-foreground">
                          {formatDate(user.created_at)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="ghost" size="sm" asChild>
                              <Link href={editAdminUserPath(user.id)}>
                                <Pencil className="h-4 w-4" />
                              </Link>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(user.id, user.name)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
